import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { openEntityTray } from '@actions/map-actions';
import { colors } from '@constants/colors';
import { detailStyles } from '@constants/mui-theme';
import { Collapse, Divider, Icon, List, ListItemButton } from '@mui';
import {
  optimizeEntitiesForListSelector
} from '@selectors/groups-selector';
import AddToGroupDialog from '@shared/dialogs/add-to-group-dialog';
import OverlapStatusDialog from '@shared/dialogs/overlap-status-dialog';
import OverlapRevokeDialog from '@shared/dialogs/overlap-revoke-dialog';
import EntityListItem from '@shared/list-item/entity-list-item';
import { findGroupType } from '@utils/group-utils';
import { pluralize } from '@utils/shared-utils';
import { optimizeSegmentForMap } from '@utils/map-utils';
import ConflictList from './conflict-list';
import MoreButton from './more-button';
import './entity-list.scss';

const EntityList = () => {
  const dispatch = useDispatch();

  const entities = useSelector(optimizeEntitiesForListSelector);
  const status = useSelector(state => state.groups.edit.group.status);
  const groupId = useSelector(state => state.groups.edit.group.id);
  const groupType = useSelector(state => state.groups.edit.group.type_name);
  const groupTypeId = useSelector(state => state.groups.edit.group.type);

  const [openItem, setOpenItem] = useState(null);

  const openTray = useCallback((type, id, segments = [], name = null, icon = null) => {
    const mapSegments = segments.map(optimizeSegmentForMap);
    const trayProps = {
      id,
      position: mapSegments[0].center,
      source: 'library',
      name,
      icon
    };
    dispatch(openEntityTray({
      ...trayProps,
      type,
      fitEntityBounds: true
    }));
  }, [dispatch]);

  const handleHeaderClick = useCallback(event => {
    const entityId = parseInt(event.currentTarget.dataset.entityId, 10);
    setOpenItem(prevOpenItem => prevOpenItem === entityId ? null : entityId);
  }, []);

  const renderMoreButton = useCallback((dataType, entity) => (
    <MoreButton
      dataType={dataType}
      groupType={groupType}
      entity={entity}
      source={`/group/${groupType}/${groupId}`}
      groupId={groupId}
    />
  ), [groupId, groupType]);

  const renderEmpty = () => {
    if (entities.length === 0) {
      return (
        <div styleName="no-records">
          <div styleName="icon">
            <Icon
              color={colors.opacity.black0_4}
              aria-hidden="true"
              style={{ fontSize: '70px' }}
            >
              view_list
            </Icon>
          </div>
          <div styleName="title">No records to show</div>
          {status === 1 && (
            <div styleName="text">
              Click <span styleName="plus-sign">+</span> at the top to add records
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const selectedGroupType = findGroupType(groupTypeId);

  return (
    <div styleName="group-entity-list">
      {renderEmpty()}
      {entities.map(entity => {
        const conflicts = entity.conflicts ? entity.conflicts.filter(
          entityId => R.pluck('id')(entities).includes(entityId)
        ) : [];
        const isOpen = openItem === entity.id;
        return (
          <div key={`container-${entity.id}`}>
            <EntityListItem
              {...entity}
              dataType={entity.type_name}
              key={entity.id}
              onClick={openTray}
              moreButton={renderMoreButton(entity.type_name, entity)}
            />
            {selectedGroupType.generates_overlaps && conflicts.length > 0 && (
              <div
                style={{
                  ...(isOpen && detailStyles.groupOpenListItem),
                  ...(!isOpen && detailStyles.groupClosedListItem)
                }}
              >
                <ListItemButton
                  onClick={handleHeaderClick}
                  data-entity-id={entity.id}
                  key={`header-${entity.id}`}
                  style={{ ...detailStyles.listItem, padding: 0, width: '100%' }}
                  aria-expanded={isOpen}
                  aria-label={`${conflicts.length} conflicts in group`}
                >
                  <div styleName="sublist-header" style={{ flex: 1 }}>
                    <div style={{ flex: 1 }}>{conflicts.length} {pluralize(conflicts.length, 'conflict')} in group</div>
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </div>
                </ListItemButton>
                {!isOpen && <Divider />}
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding style={detailStyles.listItem}>
                    <ConflictList
                      key={`list-${entity.id}`}
                      entity={entity}
                      conflictList={conflicts}
                      onClick={openTray}
                    />
                  </List>
                </Collapse>
              </div>
            )}
          </div>
        );
      })}
      <OverlapRevokeDialog />
      <OverlapStatusDialog opportunities={[]} />
      <AddToGroupDialog />
    </div>
  );
};

export default EntityList;
